import { formatCalendarDay } from '@adornis/datetime/calendarDay.js';
import { html, nothing, type TemplateResult } from 'lit';
import { firstValueFrom } from 'rxjs';
import { formatBillingPositionPrice } from '../../billingPositions/client/BillingPositionPriceInput.js';
import { BillingRhythm, translateBillingRhythm } from '../../billingPositions/entities/BillingPosition.js';
import type { Booking } from '../../bookings/entities/Booking.js';
import { SepaAPI } from '../api/SepaAPI.js';
import { clampToBookingRuntime, isSameRuntime, type MandateRuntime, runsOnNoDay } from '../api/mandateActivity.js';

// a class card saves every pupil's booking at once - the dialog names a few and counts the rest
const LISTED_CHANGES = 10;

const day = (date: Date) => formatCalendarDay(date, 'dd.MM.yyyy');

function describeChange(before: MandateRuntime, after: MandateRuntime): string {
  if (runsOnNoDay(after)) return `Läuft an keinem Tag mehr (bisher ab ${before.start ? day(before.start) : 'sofort'}).`;
  if (after.end?.getTime() !== before.end?.getTime())
    return `Endet am ${after.end ? day(after.end) : '-'} (bisher ${before.end ? day(before.end) : 'unbefristet'}).`;

  return `Beginnt am ${after.start ? day(after.start) : '-'} (bisher ${before.start ? day(before.start) : 'ohne Start'}).`;
}

/**
 * What saving these booking runtimes does to the accepted mandates of the bookings: a mandate runs
 * within its booking (`clampToBookingRuntime`, enforced by the booking hook on save), so an earlier
 * end ends it with the booking and a later start moves its start. Null when no mandate changes, so a
 * caller asks only when there is something to say.
 */
export async function describeMandateRuntimeChanges(
  bookings: Pick<Booking, '_id' | 'startDate' | 'endDate'>[],
): Promise<TemplateResult | null> {
  const bookingsByID = new Map(bookings.flatMap(booking => (booking._id ? [[booking._id, booking] as const] : [])));
  if (!bookingsByID.size) return null;

  const mandates = await firstValueFrom(
    SepaAPI.subscribeForBookings([...bookingsByID.keys()])({
      _id: 1,
      bookingID: 1,
      accepted: 1,
      name: 1,
      mandateReference: 1,
      acceptedPersonName: 1,
      amountInCents: 1,
      canEditAmount: 1,
      rhythm: 1,
      start: 1,
      end: 1,
      billedOn: 1,
    }),
  );

  const changes = mandates.flatMap(mandate => {
    const booking = bookingsByID.get(mandate.bookingID);
    if (!mandate.accepted || !booking) return [];

    const runtime = clampToBookingRuntime(mandate, booking);
    return isSameRuntime(runtime, mandate) ? [] : [{ mandate, runtime }];
  });
  if (!changes.length) return null;

  return html`
    <div class="flex flex-col gap-3">
      <p>Ein SEPA-Mandat läuft nur innerhalb seiner Buchung. Mit dieser Änderung ändern sich auch diese Mandate:</p>
      <ul class="list rounded-box bg-base-200">
        ${changes.slice(0, LISTED_CHANGES).map(
          ({ mandate, runtime }) => html`
            <li class="list-row">
              <div class="flex flex-col gap-0.5">
                <span class="font-medium">
                  ${mandate.name ?? 'Ohne Mandatsname'} · ${formatBillingPositionPrice(mandate.amountInCents)}
                  ${mandate.rhythm ? translateBillingRhythm(mandate.rhythm) : nothing}
                </span>
                <span class="text-sm text-base-content/70">
                  ${mandate.mandateReference ?? 'Ohne Mandatsreferenz'} · ${mandate.acceptedPersonName}
                </span>
                <span class="text-sm">${describeChange(mandate, runtime)}</span>
                ${
                  // for a one-time mandate the marker is reliable: without it, ending the mandate means it is never collected
                  mandate.rhythm === BillingRhythm.ONCE_CUSTOM && !mandate.billedOn
                    ? html`<span class="text-sm text-warning"
                        >Noch nicht eingezogen - danach wird es nicht mehr eingezogen.</span
                      >`
                    : nothing
                }
              </div>
            </li>
          `,
        )}
      </ul>
      ${
        changes.length > LISTED_CHANGES
          ? html`<p class="text-sm text-base-content/70">… und ${changes.length - LISTED_CHANGES} weitere Mandate.</p>`
          : nothing
      }
    </div>
  `;
}
